import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, IconButton, Stack, TextField, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";
import RichTextEditor from "../common/RichTextEditor";

const ALL_OPTION = { value: "all", label: "Select All Employees" };

export default function SendMessageNew() {
  const nav = useNavigate();
  const [empOpts, setEmpOpts] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(endpoints.selfService.directory).then((r) => {
      const list = (Array.isArray(r.data) ? r.data : r.data?.data || []).map((e) => ({
        value: e.email, label: `${e.name} (${e.email})`,
      }));
      setEmpOpts([ALL_OPTION, ...sortByLabel(list, (o) => o.label)]);
    }).catch(() => setEmpOpts([]));
  }, []);

  const totalSize = useMemo(() => files.reduce((s, f) => s + f.size, 0), [files]);

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((fs) => [...fs, ...picked]);
    e.target.value = "";
  };
  const removeFile = (i) => setFiles((fs) => fs.filter((_, idx) => idx !== i));

  const send = async (e) => {
    e.preventDefault();
    if (recipients.length === 0) return toast.error("Pick at least one recipient");
    if (!subject) return toast.error("Subject required");
    setSending(true);
    try {
      const fd = new FormData();
      const emails = recipients.some((r) => r.value === "all")
        ? empOpts.filter((o) => o.value !== "all").map((o) => o.value)
        : recipients.map((r) => r.value);
      emails.forEach((em) => fd.append("recipients[]", em));
      fd.append("subject", subject);
      fd.append("message", message);
      files.forEach((f) => fd.append("files", f));
      await api.post("/api/mailmessage/sendMessage", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Message sent to ${emails.length} recipient(s)`);
      nav("/my-sent-messages");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setSending(false); }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Compose Message"
        subtitle="Send to one or more employees"
        icon={<MailOutlineOutlinedIcon />}
      />

      <Box component="form" onSubmit={send}>
        <SectionCard accent="#6C50BF">
          <Stack spacing={2.5}>
            <Autocomplete
              multiple options={empOpts} value={recipients}
              onChange={(_, v) => {
                if (v.some((x) => x.value === "all")) setRecipients(empOpts.filter((o) => o.value !== "all"));
                else setRecipients(v);
              }}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderTags={(value, getTagProps) =>
                value.map((option, idx) => {
                  const { key, ...chipProps } = getTagProps({ index: idx });
                  return <Chip key={option.value} label={option.label} size="small" {...chipProps} />;
                })
              }
              renderInput={(p) => <TextField {...p} label="To" placeholder="Search employees by name or email" />}
            />
            <TextField fullWidth label="Subject *" value={subject} onChange={(e) => setSubject(e.target.value)} required />
            <RichTextEditor value={message} onChange={setMessage} label="Message" minHeight={220} />

            <Box>
              <Button component="label" variant="outlined" startIcon={<AttachFileOutlinedIcon />}>
                Attach files
                <input type="file" hidden multiple onChange={addFiles} />
              </Button>
              {files.length > 0 && (
                <Typography component="span" sx={{ ml: 2, fontSize: 12, color: "text.secondary" }}>
                  {files.length} file(s) • {(totalSize / 1024 / 1024).toFixed(2)} MB total
                </Typography>
              )}
            </Box>
            {files.length > 0 && (
              <Stack spacing={1}>
                {files.map((f, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1.5} sx={{ p: 1, borderRadius: 2, bgcolor: "background.default", border: "1px solid", borderColor: "divider" }}>
                    <InsertDriveFileOutlinedIcon color="primary" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{f.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{(f.size / 1024).toFixed(1)} KB</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => removeFile(i)}><CloseIcon fontSize="small" /></IconButton>
                  </Stack>
                ))}
              </Stack>
            )}

            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button onClick={() => nav(-1)}>Cancel</Button>
              <Button
                type="submit" variant="contained"
                startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendOutlinedIcon />}
                disabled={sending || recipients.length === 0 || !subject}
              >
                {sending ? "Sending…" : `Send${recipients.length ? ` to ${recipients.length}` : ""}`}
              </Button>
            </Stack>
          </Stack>
        </SectionCard>
      </Box>
    </Box>
  );
}
